import { useEffect, useMemo } from 'react';

import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { usePipelineStages } from '@/lib/crm/hooks/usePipelineStages';
import type { StageValueMapping } from '@/lib/csv-import/api';

interface UseStageValueSyncParams {
  orgId: number;
  entityTypeName: string;
  /** Whether this file's entity type can have stages (deal/contact/note). When false, no pipeline/stage fetch is issued. */
  hasStageSupport: boolean;
  /** The CSV column mapped to stages. Empty string when none is mapped. */
  column: string;
  valueMappings: StageValueMapping;
  onValueMappingsChange: (mappings: StageValueMapping) => void;
  /** Triggers the suggest-stage-values fetch for the mapped column. */
  onFetchSuggestions: () => void;
  isLoading: boolean;
  error: string | null;
  hasFetchedSuggestions: boolean;
}

/**
 * Keeps a file's stage `value_mappings` correct without any UI.
 *
 * These two effects used to live inside the StageValueMapping component. That
 * component rendered a per-value override list, which was removed: AI
 * suggestions are auto-committed into `value_mappings` by FileMappingSection
 * regardless, and anything they can't match falls back to the configured stage.
 * The list only added manual override, at the cost of a second surface competing
 * with the Fallback Values panel.
 *
 * Deleting the component outright would have taken these with it, and both are
 * load-bearing:
 *
 * 1. **Auto-fetch.** Suggestions are only fetched interactively when the user
 *    picks the stage column. A column that was set programmatically (resume, or
 *    re-suggestion) would never fetch, so `value_mappings` would stay empty and
 *    EVERY row would silently take the fallback stage.
 * 2. **Sanitize.** A stage that no longer exists, or belongs to another pipeline,
 *    must be dropped from `value_mappings` — otherwise the import submits an id
 *    the backend rejects. An empty string is kept: that is the deliberate
 *    "No stage" sentinel, not a stale id.
 */
export function useStageValueSync({
  orgId,
  entityTypeName,
  hasStageSupport,
  column,
  valueMappings,
  onValueMappingsChange,
  onFetchSuggestions,
  isLoading,
  error,
  hasFetchedSuggestions,
}: UseStageValueSyncParams): void {
  // Resolved through the same hooks AssociatedPipelineStageInput uses, so the
  // valid set never diverges from the options a picker would render. Contact
  // lifecycle stages are property-based; everything else is pipeline-based.
  const isContactLifecycle = entityTypeName === 'ahoy_contact';
  const { pipelines, isLoading: isPipelinesLoading } = usePipelines(
    hasStageSupport && !isContactLifecycle ? orgId : undefined,
    hasStageSupport && !isContactLifecycle ? entityTypeName : undefined
  );
  const { getStagesByType, isLoading: isStagesLoading } = usePipelineStages(
    hasStageSupport && isContactLifecycle ? orgId : undefined,
    hasStageSupport && isContactLifecycle ? 'ahoy_contact' : undefined
  );
  const stagesLoading = isContactLifecycle
    ? isStagesLoading
    : isPipelinesLoading;

  const validStageIds = useMemo(() => {
    const ids = new Set<string>();
    if (isContactLifecycle) {
      for (const s of getStagesByType('ahoy_contact_lifecycle_stage')) {
        if (s.entity_id) {
          ids.add(s.entity_id);
        }
      }
    } else {
      for (const p of pipelines) {
        for (const s of p.stages) {
          if (s.entity_id) {
            ids.add(s.entity_id);
          }
        }
      }
    }
    return ids;
  }, [isContactLifecycle, pipelines, getStagesByType]);

  // Only sanitize once the stage list has actually loaded and is non-empty —
  // dropping ids against an in-flight or empty set would clobber valid mappings.
  const stagesReady = !stagesLoading && validStageIds.size > 0;

  useEffect(() => {
    if (!stagesReady) {
      return;
    }
    let hasStale = false;
    const sanitized: StageValueMapping = {};
    for (const [csvValue, id] of Object.entries(valueMappings)) {
      if (id && !validStageIds.has(id)) {
        hasStale = true;
        continue;
      }
      sanitized[csvValue] = id;
    }
    if (hasStale) {
      onValueMappingsChange(sanitized);
    }
  }, [stagesReady, valueMappings, validStageIds, onValueMappingsChange]);

  // `hasFetchedSuggestions` prevents a fetch loop when the backend returns an
  // empty unique_values list — without it the condition stays true after each
  // fetch and hammers the API.
  useEffect(() => {
    if (column && !hasFetchedSuggestions && !isLoading && !error) {
      onFetchSuggestions();
    }
  }, [column, hasFetchedSuggestions, isLoading, error, onFetchSuggestions]);
}
